import {
  ComponentPropsWithRef,
  createContext,
  forwardRef,
  useContext,
  useMemo,
  useState,
} from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import { useTranslation } from 'react-i18next';
import { ExpandMore, UnfoldMore } from '@mui/icons-material';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link as MuiLink,
} from '@mui/material';
import Loader from '../../../components/Loader';
import { FilterPopover } from '../../internalSearch/components/FilterPopover';
import { mapAgreementStatus } from '../../internalSearch/util/agreement-status';
import {
  arrowStyling,
  arrowStylingDefault,
  SortableHeaderButton,
} from '../../../components/TableHeaderButton';
import { getSortedData, Order } from '../../../hooks/usePersistentSorting';
import {
  MasterListFilter,
  MasterListFilterKeys,
  MasterListJournal,
  MaterialListStatus,
} from '../../../types/MasterList.types';
import dayjs from 'dayjs';
import styled from '@emotion/styled';
import { statusColors } from '../utils/StatusColor';
import { getStatusLabel, isStatusVerified } from '../utils/status';

interface JournalsTableContext {
  onJournalClick: (journal: MasterListJournal) => void;
}

const JournalsTableContext = createContext<JournalsTableContext>({
  onJournalClick: () => {},
});

const InnerTableBody = forwardRef<HTMLTableSectionElement>((props, ref) => (
  <TableBody {...props} ref={ref} />
));
InnerTableBody.displayName = 'TableBody';

const InnerTableHead = forwardRef<HTMLTableSectionElement>((props, ref) => (
  <TableHead {...props} ref={ref} />
));
InnerTableHead.displayName = 'TableHead';

const InnerTableRow = forwardRef<
  HTMLTableRowElement,
  ComponentPropsWithRef<typeof TableRow> & { item: MasterListJournal }
>(({ item: journal, style, ...props }, ref) => {
  const { t } = useTranslation();
  const { onJournalClick } = useContext(JournalsTableContext);
  const rowLabel = journal.name
    ? t('agreements.editJournalRow', { name: journal.name })
    : t('agreements.editJournalRowUnnamed');

  return (
    <TableRow
      tabIndex={0}
      aria-label={rowLabel}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onJournalClick(journal);
        }
      }}
      {...props}
      ref={ref}
      hover
      onClick={() => onJournalClick(journal)}
      style={style}
      sx={{
        cursor: 'pointer',
        ...(!isStatusVerified(journal.status)
          ? {
              backgroundColor: 'var(--color-very-pale-orange)',
              '&.MuiTableRow-hover:hover': {
                backgroundColor: 'var(--color-very-pale-orange)',
              },
            }
          : {}),
        '&:focus-visible': {
          outline: '2px solid var(--color-scifree-teal)',
          outlineOffset: '-2px',
        },
      }}
    />
  );
});
InnerTableRow.displayName = 'TableRow';

const TableWrapper = (props: ComponentPropsWithRef<typeof Table>) => {
  const { t } = useTranslation();
  return (
    <Table
      {...props}
      sx={{ minWidth: 960, tableLayout: 'fixed' }}
      aria-label={t('masterList.tableAriaLabel') as string}
    />
  );
};

export const StyledStatusChip = styled(Chip)<{
  status: MaterialListStatus | undefined;
}>`
  && {
    background-color: ${(props) =>
      props.status ? statusColors[props.status] : 'transparent'};
    text-transform: capitalize;
  }
`;

interface JournalsTableDesktopProps {
  journals: MasterListJournal[];
  filters: MasterListFilter;
  selectedFilters: MasterListFilter;
  isLoading?: boolean;
  onToggleFilter: (type: MasterListFilterKeys, value: string) => void;
  onJournalClick: (journal: MasterListJournal) => void;
}

export const JournalsTableDesktop = ({
  journals,
  filters,
  selectedFilters,
  isLoading = false,
  onToggleFilter,
  onJournalClick,
}: JournalsTableDesktopProps) => {
  const { t } = useTranslation();

  const [openFilter, setOpenFilter] = useState<{
    key: MasterListFilterKeys;
    anchor: HTMLElement;
  } | null>(null);

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof MasterListJournal | null>(
    null
  );

  const handleRequestSort = (property: keyof MasterListJournal) => {
    if (orderBy === property) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(property);
      setOrder('asc');
    }
  };

  const getArrowIcon = (key: keyof MasterListJournal) => {
    if (orderBy === key) {
      return order === 'asc' ? (
        <ExpandMore fontSize="small" style={arrowStyling} />
      ) : (
        <ExpandMore fontSize="small" style={arrowStylingDefault} />
      );
    }
    return <UnfoldMore fontSize="small" style={arrowStylingDefault} />;
  };

  const sortedJournals = useMemo(
    () => (orderBy ? getSortedData(journals, orderBy, order) : journals),
    [journals, orderBy, order]
  );

  return (
    <JournalsTableContext.Provider value={{ onJournalClick }}>
      <TableVirtuoso
        style={{ height: '640px' }}
        components={{
          Table: TableWrapper,
          TableBody: InnerTableBody,
          TableHead: InnerTableHead,
          TableRow: InnerTableRow,
          EmptyPlaceholder: () =>
            isLoading ? (
              <tr>
                <td colSpan={10}>
                  <Loader />
                </td>
              </tr>
            ) : null,
        }}
        fixedHeaderContent={() => (
          <TableRow>
            <TableCell sx={{ width: '12%' }}>
              {t('masterList.columnTitle')}
            </TableCell>
            <TableCell sx={{ width: '9%' }}>
              <FilterPopover
                label={t('masterList.columnPublisher')}
                options={filters.publishers}
                selectedOptions={selectedFilters.publishers}
                onToggle={(publisher) =>
                  onToggleFilter('publishers', publisher)
                }
                anchorEl={
                  openFilter?.key === 'publishers' ? openFilter.anchor : null
                }
                open={openFilter?.key === 'publishers'}
                onOpen={(e) =>
                  setOpenFilter({ key: 'publishers', anchor: e.currentTarget })
                }
                onClose={() => setOpenFilter(null)}
              />
            </TableCell>
            <TableCell sx={{ width: '9%' }}>
              <FilterPopover
                label={t('masterList.columnImprint')}
                options={filters.imprints}
                selectedOptions={selectedFilters.imprints}
                onToggle={(imprint) => onToggleFilter('imprints', imprint)}
                anchorEl={
                  openFilter?.key === 'imprints' ? openFilter.anchor : null
                }
                open={openFilter?.key === 'imprints'}
                onOpen={(e) =>
                  setOpenFilter({ key: 'imprints', anchor: e.currentTarget })
                }
                onClose={() => setOpenFilter(null)}
              />
            </TableCell>
            <TableCell sx={{ width: '9%' }}>
              <FilterPopover
                label={t('masterList.columnLicenseOption')}
                options={filters.licenses}
                selectedOptions={selectedFilters.licenses}
                onToggle={(license) => onToggleFilter('licenses', license)}
                anchorEl={
                  openFilter?.key === 'licenses' ? openFilter.anchor : null
                }
                open={openFilter?.key === 'licenses'}
                onOpen={(e) =>
                  setOpenFilter({ key: 'licenses', anchor: e.currentTarget })
                }
                onClose={() => setOpenFilter(null)}
              />
            </TableCell>
            <TableCell sx={{ width: '9%' }}>
              <FilterPopover
                label={t('masterList.columnPublishingModel')}
                options={filters.publishingModels}
                selectedOptions={selectedFilters.publishingModels}
                onToggle={(model) => onToggleFilter('publishingModels', model)}
                anchorEl={
                  openFilter?.key === 'publishingModels'
                    ? openFilter.anchor
                    : null
                }
                open={openFilter?.key === 'publishingModels'}
                onOpen={(e) =>
                  setOpenFilter({
                    key: 'publishingModels',
                    anchor: e.currentTarget,
                  })
                }
                onClose={() => setOpenFilter(null)}
              />
            </TableCell>
            <TableCell sx={{ width: '7%' }}>
              {t('masterList.columnIssnE')}
            </TableCell>
            <TableCell sx={{ width: '7%' }}>
              {t('masterList.columnIssnP')}
            </TableCell>
            <TableCell sx={{ width: '9%' }}>
              {t('masterList.columnSubject')}
            </TableCell>
            <TableCell sx={{ width: '9%' }}>
              <SortableHeaderButton
                variant="secondary"
                onClick={() => handleRequestSort('updated_at')}
                isSorted={orderBy === 'updated_at'}
                sortDirection={order}
              >
                {t('masterList.columnUpdated')}
                {getArrowIcon('updated_at')}
              </SortableHeaderButton>
            </TableCell>
            <TableCell sx={{ width: '7%' }}>
              <FilterPopover
                label={t('masterList.columnJournalStatus')}
                options={filters.statuses}
                selectedOptions={selectedFilters.statuses}
                onToggle={(status) => onToggleFilter('statuses', status)}
                anchorEl={
                  openFilter?.key === 'statuses' ? openFilter.anchor : null
                }
                open={openFilter?.key === 'statuses'}
                onOpen={(e) =>
                  setOpenFilter({ key: 'statuses', anchor: e.currentTarget })
                }
                onClose={() => setOpenFilter(null)}
                optionLabelMapper={mapAgreementStatus}
              />
            </TableCell>
          </TableRow>
        )}
        data={sortedJournals}
        itemContent={(_index, journal) => {
          return (
            <>
              <TableCell>
                <MuiLink
                  href={journal.url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {journal.name}
                </MuiLink>
              </TableCell>
              <TableCell>{journal.publisher ?? '-'}</TableCell>
              <TableCell>{journal.imprint ?? '-'}</TableCell>
              <TableCell>{journal.licenses?.join(', ') || '-'}</TableCell>
              <TableCell>{journal.publishing_model ?? '-'}</TableCell>
              <TableCell>{journal.issn_e ?? '-'}</TableCell>
              <TableCell>{journal.issn_p ?? '-'}</TableCell>
              <TableCell>{journal.subjects ?? '-'}</TableCell>
              <TableCell>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    alignItems: 'start',
                  }}
                >
                  <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                    {dayjs.utc(journal.updated_at).local().format('YYYY-MM-DD')}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      width: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={journal.updated_by?.trim() || '-'}
                  >
                    {journal.updated_by?.trim() || '-'}
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                {journal.status != null ? (
                  <StyledStatusChip
                    status={journal.status}
                    size="small"
                    label={getStatusLabel(journal.status)}
                    sx={{ margin: 'auto' }}
                  />
                ) : (
                  '-'
                )}
              </TableCell>
            </>
          );
        }}
      />
    </JournalsTableContext.Provider>
  );
};
